// Centralized Data Management for DRIVE HAUS
// Handles localStorage operations with error handling and data normalization

const STORAGE_KEYS = {
  VEHICLES: 'drivehaus_vehicles',
  USERS: 'drivehaus_users',
  INQUIRIES: 'drivehaus_inquiries',
  TEST_DRIVES: 'drivehaus_test_drives',
  LEASE_APPLICATIONS: 'drivehaus_lease_applications',
  FAVORITES: 'drivehaus_favorites',
  WEBSITE_SETTINGS: 'drivehaus_website_settings',
  CURRENT_USER: 'drivehaus_current_user'
};

// Safe localStorage operations
const storage = {
  get: (key) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error(`Error reading ${key} from localStorage:`, error);
      return null;
    }
  },
  
  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error(`Error writing ${key} to localStorage:`, error);
      return false;
    }
  },
  
  remove: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Error removing ${key} from localStorage:`, error);
      return false;
    }
  }
};

// Vehicle data structure standardization
const normalizeVehicle = (vehicle) => {
  return {
    id: vehicle.id || Date.now(),
    make: vehicle.make || vehicle.brand || '',
    model: vehicle.model || '',
    variant: vehicle.variant || '',
    year: vehicle.year || new Date().getFullYear(),
    price: vehicle.price || 0,
    leasePrice: vehicle.leasePrice || 0,
    downPayment: vehicle.downPayment || 0,
    leaseTerm: vehicle.leaseTerm || 36,
    purchaseType: vehicle.purchaseType || 'both', // 'cash', 'lease', 'both'
    mileage: vehicle.mileage || 0,
    transmission: vehicle.transmission || 'Automatic',
    fuel: vehicle.fuel || 'Petrol',
    engine: vehicle.engine || '',
    bodyType: vehicle.bodyType || vehicle.type || 'Sedan',
    condition: vehicle.condition || 'Used',
    color: vehicle.color || '',
    description: vehicle.description || '',
    features: vehicle.features || [],
    images: vehicle.images || (vehicle.image ? [vehicle.image] : []),
    featured: vehicle.featured || false,
    hotSelling: vehicle.hotSelling || false,
    status: vehicle.status || 'available', // 'available', 'reserved', 'sold', 'hidden'
    available: vehicle.available !== undefined ? vehicle.available : true,
    createdAt: vehicle.createdAt || new Date().toISOString(),
    updatedAt: vehicle.updatedAt || new Date().toISOString()
  };
};

// Vehicle CRUD operations
export const vehicleManager = {
  getAll: () => {
    const vehicles = storage.get(STORAGE_KEYS.VEHICLES);
    return vehicles ? vehicles.map(normalizeVehicle) : [];
  },
  
  getById: (id) => {
    const vehicles = vehicleManager.getAll();
    return vehicles.find(v => v.id === id) || null;
  },
  
  add: (vehicleData) => {
    const vehicles = vehicleManager.getAll();
    const newVehicle = normalizeVehicle({
      ...vehicleData,
      id: Date.now(),
      createdAt: new Date().toISOString()
    });
    vehicles.push(newVehicle);
    storage.set(STORAGE_KEYS.VEHICLES, vehicles);
    return newVehicle;
  },
  
  update: (id, updates) => {
    const vehicles = vehicleManager.getAll();
    const index = vehicles.findIndex(v => v.id === id);
    if (index === -1) return null;
    
    vehicles[index] = normalizeVehicle({
      ...vehicles[index],
      ...updates,
      updatedAt: new Date().toISOString()
    });
    storage.set(STORAGE_KEYS.VEHICLES, vehicles);
    return vehicles[index];
  },
  
  delete: (id) => {
    const vehicles = vehicleManager.getAll();
    const filtered = vehicles.filter(v => v.id !== id);
    storage.set(STORAGE_KEYS.VEHICLES, filtered);
    return filtered.length !== vehicles.length;
  },
  
  getFeatured: () => {
    return vehicleManager.getAll().filter(v => v.featured && v.status === 'available');
  },
  
  getHotSelling: () => {
    return vehicleManager.getAll().filter(v => v.hotSelling && v.status === 'available');
  },
  
  getByPurchaseType: (type) => {
    const vehicles = vehicleManager.getAll();
    if (type === 'cash') {
      return vehicles.filter(v => 
        (v.purchaseType === 'cash' || v.purchaseType === 'both') && v.status === 'available'
      );
    } else if (type === 'lease') {
      return vehicles.filter(v => 
        (v.purchaseType === 'lease' || v.purchaseType === 'both') && v.status === 'available'
      );
    }
    return vehicles;
  },
  
  search: (query) => {
    const vehicles = vehicleManager.getAll();
    const lowerQuery = query.toLowerCase();
    return vehicles.filter(v => 
      v.status === 'available' && (
        v.make.toLowerCase().includes(lowerQuery) ||
        v.model.toLowerCase().includes(lowerQuery) ||
        v.year.toString().includes(lowerQuery) ||
        v.description.toLowerCase().includes(lowerQuery)
      )
    );
  },
  
  filter: (filters) => {
    let vehicles = vehicleManager.getAll();
    
    if (filters.make) {
      vehicles = vehicles.filter(v => v.make === filters.make);
    }
    if (filters.model) {
      vehicles = vehicles.filter(v => v.model === filters.model);
    }
    if (filters.year) {
      vehicles = vehicles.filter(v => v.year === filters.year);
    }
    if (filters.minPrice) {
      vehicles = vehicles.filter(v => v.price >= filters.minPrice);
    }
    if (filters.maxPrice) {
      vehicles = vehicles.filter(v => v.price <= filters.maxPrice);
    }
    if (filters.purchaseType) {
      vehicles = vehicles.filter(v => 
        v.purchaseType === filters.purchaseType || v.purchaseType === 'both'
      );
    }
    if (filters.status) {
      vehicles = vehicles.filter(v => v.status === filters.status);
    }
    if (filters.featured !== undefined) {
      vehicles = vehicles.filter(v => v.featured === filters.featured);
    }
    if (filters.hotSelling !== undefined) {
      vehicles = vehicles.filter(v => v.hotSelling === filters.hotSelling);
    }
    if (filters.bodyType) {
      vehicles = vehicles.filter(v => v.bodyType === filters.bodyType);
    }
    if (filters.transmission) {
      vehicles = vehicles.filter(v => v.transmission === filters.transmission);
    }
    if (filters.fuel) {
      vehicles = vehicles.filter(v => v.fuel === filters.fuel);
    }
    
    return vehicles;
  }
};

// User management
export const userManager = {
  getAll: () => {
    return storage.get(STORAGE_KEYS.USERS) || [];
  },
  
  getById: (id) => {
    const users = userManager.getAll();
    return users.find(u => u.id === id) || null;
  },
  
  getByEmail: (email) => {
    const users = userManager.getAll();
    return users.find(u => u.email === email) || null;
  },
  
  add: (userData) => {
    const users = userManager.getAll();
    const newUser = {
      ...userData,
      id: userData.id || Date.now(),
      role: userData.role || 'customer',
      registrationDate: userData.registrationDate || new Date().toLocaleDateString(),
      createdAt: new Date().toISOString()
    };
    users.push(newUser);
    storage.set(STORAGE_KEYS.USERS, users);
    return newUser;
  },
  
  update: (id, updates) => {
    const users = userManager.getAll();
    const index = users.findIndex(u => u.id === id);
    if (index === -1) return null;
    
    users[index] = { ...users[index], ...updates };
    storage.set(STORAGE_KEYS.USERS, users);
    return users[index];
  },
  
  delete: (id) => {
    const users = userManager.getAll();
    const filtered = users.filter(u => u.id !== id);
    storage.set(STORAGE_KEYS.USERS, filtered);
    return filtered.length !== users.length;
  },
  
  setCurrentUser: (user) => {
    storage.set(STORAGE_KEYS.CURRENT_USER, user);
  },
  
  getCurrentUser: () => {
    return storage.get(STORAGE_KEYS.CURRENT_USER);
  },
  
  logout: () => {
    storage.remove(STORAGE_KEYS.CURRENT_USER);
  }
};

// Inquiry management
export const inquiryManager = {
  getAll: () => {
    return storage.get(STORAGE_KEYS.INQUIRIES) || [];
  },
  
  getById: (id) => {
    const inquiries = inquiryManager.getAll();
    return inquiries.find(i => i.id === id) || null;
  },
  
  getByCustomer: (customerEmail) => {
    return inquiryManager.getAll().filter(i => i.customerEmail === customerEmail);
  },
  
  getByVehicle: (vehicleId) => {
    return inquiryManager.getAll().filter(i => i.vehicleId === vehicleId);
  },
  
  add: (inquiryData) => {
    const inquiries = inquiryManager.getAll();
    const newInquiry = {
      ...inquiryData,
      id: inquiryData.id || Date.now(),
      status: inquiryData.status || 'new',
      date: inquiryData.date || new Date().toLocaleDateString(),
      notes: inquiryData.notes || '',
      internalNotes: inquiryData.internalNotes || [],
      createdAt: new Date().toISOString()
    };
    inquiries.push(newInquiry);
    storage.set(STORAGE_KEYS.INQUIRIES, inquiries);
    return newInquiry;
  },
  
  update: (id, updates) => {
    const inquiries = inquiryManager.getAll();
    const index = inquiries.findIndex(i => i.id === id);
    if (index === -1) return null;
    
    inquiries[index] = { ...inquiries[index], ...updates };
    storage.set(STORAGE_KEYS.INQUIRIES, inquiries);
    return inquiries[index];
  },
  
  addInternalNote: (id, note, adminName) => {
    const inquiries = inquiryManager.getAll();
    const index = inquiries.findIndex(i => i.id === id);
    if (index === -1) return null;
    
    const newNote = {
      text: note,
      addedBy: adminName || 'Admin',
      timestamp: new Date().toISOString()
    };
    
    inquiries[index].internalNotes = [...(inquiries[index].internalNotes || []), newNote];
    storage.set(STORAGE_KEYS.INQUIRIES, inquiries);
    return inquiries[index];
  },
  
  delete: (id) => {
    const inquiries = inquiryManager.getAll();
    const filtered = inquiries.filter(i => i.id !== id);
    storage.set(STORAGE_KEYS.INQUIRIES, filtered);
    return filtered.length !== inquiries.length;
  }
};

// Test drive management
export const testDriveManager = {
  getAll: () => {
    return storage.get(STORAGE_KEYS.TEST_DRIVES) || [];
  },
  
  getById: (id) => {
    const testDrives = testDriveManager.getAll();
    return testDrives.find(t => t.id === id) || null;
  },
  
  getByCustomer: (customerEmail) => {
    return testDriveManager.getAll().filter(t => t.customerEmail === customerEmail);
  },
  
  getByVehicle: (vehicleId) => {
    return testDriveManager.getAll().filter(t => t.vehicleId === vehicleId);
  },
  
  add: (testDriveData) => {
    const testDrives = testDriveManager.getAll();
    const newTestDrive = {
      ...testDriveData,
      id: testDriveData.id || Date.now(),
      status: testDriveData.status || 'pending',
      notes: testDriveData.notes || '',
      internalNotes: testDriveData.internalNotes || [],
      createdAt: new Date().toISOString()
    };
    testDrives.push(newTestDrive);
    storage.set(STORAGE_KEYS.TEST_DRIVES, testDrives);
    return newTestDrive;
  },
  
  update: (id, updates) => {
    const testDrives = testDriveManager.getAll();
    const index = testDrives.findIndex(t => t.id === id);
    if (index === -1) return null;
    
    testDrives[index] = { ...testDrives[index], ...updates };
    storage.set(STORAGE_KEYS.TEST_DRIVES, testDrives);
    return testDrives[index];
  },
  
  addInternalNote: (id, note, adminName) => {
    const testDrives = testDriveManager.getAll();
    const index = testDrives.findIndex(t => t.id === id);
    if (index === -1) return null;
    
    const newNote = {
      text: note,
      addedBy: adminName || 'Admin',
      timestamp: new Date().toISOString()
    };
    
    testDrives[index].internalNotes = [...(testDrives[index].internalNotes || []), newNote];
    storage.set(STORAGE_KEYS.TEST_DRIVES, testDrives);
    return testDrives[index];
  },
  
  delete: (id) => {
    const testDrives = testDriveManager.getAll();
    const filtered = testDrives.filter(t => t.id !== id);
    storage.set(STORAGE_KEYS.TEST_DRIVES, filtered);
    return filtered.length !== testDrives.length;
  }
};

// Lease application management
export const leaseManager = {
  getAll: () => {
    return storage.get(STORAGE_KEYS.LEASE_APPLICATIONS) || [];
  },
  
  getById: (id) => {
    const applications = leaseManager.getAll();
    return applications.find(a => a.id === id) || null;
  },
  
  getByCustomer: (customerEmail) => {
    return leaseManager.getAll().filter(a => a.customerEmail === customerEmail);
  },
  
  add: (applicationData) => {
    const applications = leaseManager.getAll();
    const newApplication = {
      ...applicationData,
      id: applicationData.id || Date.now(),
      status: applicationData.status || 'new',
      createdAt: new Date().toISOString()
    };
    applications.push(newApplication);
    storage.set(STORAGE_KEYS.LEASE_APPLICATIONS, applications);
    return newApplication;
  },
  
  update: (id, updates) => {
    const applications = leaseManager.getAll();
    const index = applications.findIndex(a => a.id === id);
    if (index === -1) return null;
    
    applications[index] = { ...applications[index], ...updates };
    storage.set(STORAGE_KEYS.LEASE_APPLICATIONS, applications);
    return applications[index];
  },
  
  delete: (id) => {
    const applications = leaseManager.getAll();
    const filtered = applications.filter(a => a.id !== id);
    storage.set(STORAGE_KEYS.LEASE_APPLICATIONS, filtered);
    return filtered.length !== applications.length;
  }
};

// Favorites management
export const favoritesManager = {
  getAll: () => {
    return storage.get(STORAGE_KEYS.FAVORITES) || [];
  },
  
  getByCustomer: (customerEmail) => {
    return favoritesManager.getAll().filter(f => f.customerEmail === customerEmail);
  },
  
  add: (favoriteData) => {
    const favorites = favoritesManager.getAll();
    const newFavorite = {
      ...favoriteData,
      id: favoriteData.id || Date.now(),
      savedDate: favoriteData.savedDate || new Date().toLocaleDateString(),
      createdAt: new Date().toISOString()
    };
    favorites.push(newFavorite);
    storage.set(STORAGE_KEYS.FAVORITES, favorites);
    return newFavorite;
  },
  
  remove: (customerEmail, vehicleId) => {
    const favorites = favoritesManager.getAll();
    const filtered = favorites.filter(f => 
      !(f.customerEmail === customerEmail && f.vehicleId === vehicleId)
    );
    storage.set(STORAGE_KEYS.FAVORITES, filtered);
    return filtered.length !== favorites.length;
  },
  
  isFavorite: (customerEmail, vehicleId) => {
    const favorites = favoritesManager.getByCustomer(customerEmail);
    return favorites.some(f => f.vehicleId === vehicleId);
  }
};

// Website settings management
export const settingsManager = {
  get: () => {
    return storage.get(STORAGE_KEYS.WEBSITE_SETTINGS) || {
      businessName: 'Drive Haus Isl',
      tagline: 'DRIVEN BY EXCELLENCE',
      phone: '0309-5111185',
      whatsapp: '0309-5111185',
      email: 'info@drivehaus.com',
      address: null,
      city: 'Islamabad',
      state: null,
      zipCode: null,
      hours: {
        weekdays: 'Mon-Fri: 9AM-6PM',
        saturday: 'Saturday: 10AM-4PM',
        sunday: 'Sunday: Closed'
      },
      social: {
        facebook: '#',
        instagram: '#',
        twitter: '#',
        linkedin: '#'
      },
      hero: {
        heading: 'YOUR DREAM CAR. YOUR WAY.',
        subheading: 'Premium vehicles. Flexible ownership. Exceptional service.',
        ctaPrimary: 'EXPLORE INVENTORY',
        ctaSecondary: 'CALCULATE LEASE'
      },
      financing: {
        defaultInterestRate: 0.05,
        defaultTerm: 36,
        minDownPaymentPercent: 0.10,
        availableTerms: [12, 24, 36, 48, 60]
      }
    };
  },
  
  update: (settings) => {
    const current = settingsManager.get();
    const updated = { ...current, ...settings };
    storage.set(STORAGE_KEYS.WEBSITE_SETTINGS, updated);
    return updated;
  }
};

// Statistics for admin dashboard
export const getStatistics = () => {
  const vehicles = vehicleManager.getAll();
  const users = userManager.getAll();
  const inquiries = inquiryManager.getAll();
  const testDrives = testDriveManager.getAll();
  const leaseApplications = leaseManager.getAll();
  
  return {
    vehicles: {
      total: vehicles.length,
      available: vehicles.filter(v => v.status === 'available').length,
      sold: vehicles.filter(v => v.status === 'sold').length,
      reserved: vehicles.filter(v => v.status === 'reserved').length,
      cash: vehicles.filter(v => v.purchaseType === 'cash' || v.purchaseType === 'both').length,
      lease: vehicles.filter(v => v.purchaseType === 'lease' || v.purchaseType === 'both').length,
      featured: vehicles.filter(v => v.featured).length,
      hotSelling: vehicles.filter(v => v.hotSelling).length
    },
    customers: {
      total: users.length,
      new: users.filter(u => {
        const daysSinceRegistration = (new Date() - new Date(u.createdAt)) / (1000 * 60 * 60 * 24);
        return daysSinceRegistration <= 30;
      }).length
    },
    inquiries: {
      total: inquiries.length,
      new: inquiries.filter(i => i.status === 'new').length,
      pending: inquiries.filter(i => ['new', 'contacted', 'follow-up'].includes(i.status)).length
    },
    testDrives: {
      total: testDrives.length,
      pending: testDrives.filter(t => t.status === 'pending').length,
      upcoming: testDrives.filter(t => 
        t.status === 'confirmed' && new Date(t.requestedDate) >= new Date()
      ).length
    },
    leaseApplications: {
      total: leaseApplications.length,
      pending: leaseApplications.filter(l => ['new', 'under_review'].includes(l.status)).length
    }
  };
};

const dataManager = {
  vehicleManager,
  userManager,
  inquiryManager,
  testDriveManager,
  leaseManager,
  favoritesManager,
  settingsManager,
  getStatistics,
  STORAGE_KEYS
};

export default dataManager;